import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import { PLATFORM_ICONS, PLATFORM_COLORS } from '../pages/SocialSyncPage';
import {
  Check, Loader2, Globe, ChevronDown, ChevronUp,
  ExternalLink, Copy, CheckCircle2, XCircle, AlertCircle
} from 'lucide-react';

export function CrossPostSelector({ onSelectPlatforms, onCaptionsChange }) {
  const { getAuthHeaders } = useAuth();
  const [connections, setConnections] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    try {
      const res = await fetch(`${API}/social/connections`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setConnections(data.connections || []);
        // Auto-select platforms with auto_post enabled
        const autoSelect = new Set();
        (data.connections || []).forEach(c => {
          if (c.auto_post) autoSelect.add(c.platform);
        });
        setSelected(autoSelect);
        onSelectPlatforms?.(Array.from(autoSelect));
      }
    } catch {} finally { setLoading(false); }
  };

  const togglePlatform = (platform) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(platform)) next.delete(platform);
      else next.add(platform);
      onSelectPlatforms?.(Array.from(next));
      return next;
    });
  };

  if (loading) return null;
  if (connections.length === 0) return null;

  return (
    <div className="mt-3" data-testid="cross-post-selector">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground/80 transition-colors w-full"
      >
        <Globe className="w-3.5 h-3.5" />
        <span>Also post to{selected.size > 0 ? ` (${selected.size})` : ''}...</span>
        {expanded ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
      </button>

      {expanded && (
        <div className="mt-2 space-y-1.5">
          {connections.map(conn => (
            <button
              key={conn.connection_id}
              onClick={() => togglePlatform(conn.platform)}
              className={`flex items-center gap-3 w-full p-2.5 rounded-lg transition-all ${
                selected.has(conn.platform)
                  ? 'bg-blue-500/10 border border-blue-500/20'
                  : 'bg-muted/20 border border-border hover:border-border'
              }`}
              data-testid={`cross-post-toggle-${conn.platform}`}
            >
              <div className={`w-7 h-7 rounded-md ${PLATFORM_COLORS[conn.platform] || 'bg-muted'} flex items-center justify-center text-white scale-75`}>
                {PLATFORM_ICONS[conn.platform]}
              </div>
              <span className="text-sm flex-1 text-left">{conn.account_name || conn.platform}</span>
              <Badge className={conn.status === 'connected' ? 'bg-green-500/10 text-green-400 border-green-500/20 text-[10px]' : 'bg-blue-500/10 text-blue-400 border-blue-500/20 text-[10px]'}>
                {conn.status === 'connected' ? 'Auto' : 'Share'}
              </Badge>
              <div className={`w-5 h-5 rounded-md flex items-center justify-center ${
                selected.has(conn.platform) ? 'bg-blue-500 text-white' : 'border border-border'
              }`}>
                {selected.has(conn.platform) && <Check className="w-3 h-3" />}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function CrossPostResults({ postId, results, onClose }) {
  if (!results || results.length === 0) return null;

  const handleSmartShare = (result) => {
    if (result.share_url) {
      window.open(result.share_url, '_blank', 'width=600,height=400');
    } else {
      // Copy caption + link for manual sharing
      const text = `${result.caption || ''}\n\n${result.post_url || ''}`.trim();
      navigator.clipboard.writeText(text).then(() => toast.success('Caption & link copied!')).catch(() => toast.error('Copy failed'));
    }
  };

  return (
    <Card className="bg-muted/20 border-border p-4 mt-4" data-testid="cross-post-results">
      <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
        <Globe className="w-4 h-4 text-blue-400" />Cross-Post Results
      </h3>
      <div className="space-y-2">
        {results.map(r => (
          <div key={r.cross_post_id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/20">
            <div className={`w-7 h-7 rounded-md ${PLATFORM_COLORS[r.platform] || 'bg-muted'} flex items-center justify-center text-white scale-75`}>
              {PLATFORM_ICONS[r.platform]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm">{r.platform}</p>
            </div>
            {r.status === 'posted' && (
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                <CheckCircle2 className="w-3 h-3 mr-1" />Posted
              </Badge>
            )}
            {r.status === 'smart_share' && (
              <Button size="sm" variant="outline" className="text-xs border-blue-500/30 text-blue-400 h-7"
                onClick={() => handleSmartShare(r)} data-testid={`smart-share-${r.platform}`}>
                <ExternalLink className="w-3 h-3 mr-1" />Share Now
              </Button>
            )}
            {r.status === 'failed' && (
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                <XCircle className="w-3 h-3 mr-1" />Failed
              </Badge>
            )}
            {r.status === 'not_connected' && (
              <Badge className="bg-muted/40 text-muted-foreground border-border/30">
                <AlertCircle className="w-3 h-3 mr-1" />Not Connected
              </Badge>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
